import React, { useState } from 'react';
import { getTenant, formatCurrencyAmount } from '../../../theme/tokens';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, CheckCircleIcon, ClockIcon, AlertCircleIcon, XCircleIcon, DownloadIcon, SearchIcon, FilterIcon, MoreHorizontalIcon, SendIcon, RefreshCwIcon, FileTextIcon, InfoIcon, XIcon } from 'lucide-react';
import { PaymentItem, BulkPaymentBatch } from './BulkPayments';
export function BulkPaymentDetail() {
  const {
    t
  } = useTranslation('payments');
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [showReminderModal, setShowReminderModal] = useState(false);
  // Mock batch data
  const batch: BulkPaymentBatch = {
    id: id || '1',
    name: 'December Invoices',
    createdAt: '2023-12-15T09:30:00',
    totalAmount: 12450.75,
    currency: getTenant().currency.code,
    totalItems: 45,
    completedItems: 38,
    status: 'completed',
    collectionRate: 84.4
  };
  // Mock payment items
  const paymentItems: PaymentItem[] = [{
    id: '1',
    customerName: 'John Smith',
    email: 'john.smith@example.com',
    phone: '+1 (555) 123-4567',
    amount: '349.99',
    currency: getTenant().currency.code,
    description: 'Monthly subscription - Premium Plan',
    reference: 'INV-2023-12-001',
    status: 'paid'
  }, {
    id: '2',
    customerName: 'Sarah Johnson',
    email: 'sarah.j@example.com',
    phone: '+1 (555) 987-6543',
    amount: '199.50',
    currency: getTenant().currency.code,
    description: 'Consulting services - December',
    reference: 'INV-2023-12-002',
    status: 'paid'
  }, {
    id: '3',
    customerName: 'Michael Brown',
    email: 'mbrown@example.com',
    phone: '+1 (555) 456-7890',
    amount: '599.00',
    currency: getTenant().currency.code,
    description: 'Website development - Phase 1',
    reference: 'INV-2023-12-003',
    status: 'pending'
  }, {
    id: '4',
    customerName: 'Emily Wilson',
    email: 'e.wilson@example.com',
    phone: '+1 (555) 234-5678',
    amount: '149.99',
    currency: getTenant().currency.code,
    description: 'Monthly subscription - Basic Plan',
    reference: 'INV-2023-12-004',
    status: 'paid'
  }, {
    id: '5',
    customerName: 'David Lee',
    email: 'david.lee@example.com',
    phone: '+1 (555) 876-5432',
    amount: '299.99',
    currency: getTenant().currency.code,
    description: 'Marketing services - December',
    reference: 'INV-2023-12-005',
    status: 'expired'
  }, {
    id: '6',
    customerName: 'Jennifer Garcia',
    email: 'j.garcia@example.com',
    phone: '+1 (555) 345-6789',
    amount: '499.00',
    currency: getTenant().currency.code,
    description: 'Product photography - Package B',
    reference: 'INV-2023-12-006',
    status: 'paid'
  }, {
    id: '7',
    customerName: 'Robert Martinez',
    email: 'r.martinez@example.com',
    phone: '+1 (555) 765-4321',
    amount: '799.50',
    currency: getTenant().currency.code,
    description: 'Equipment rental - December',
    reference: 'INV-2023-12-007',
    status: 'paid'
  }];
  // Filter payments based on search and status filter
  const filteredPayments = paymentItems.filter(item => {
    const matchesSearch = item.customerName.toLowerCase().includes(searchTerm.toLowerCase()) || item.email.toLowerCase().includes(searchTerm.toLowerCase()) || item.description.toLowerCase().includes(searchTerm.toLowerCase()) || item.reference.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter ? item.status === statusFilter : true;
    return matchesSearch && matchesStatus;
  });
  // Count payments by status
  const statusCounts = paymentItems.reduce((acc, item) => {
    acc[item.status] = (acc[item.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'paid':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-8011">
            <CheckCircleIcon size={12} className="mr-1" data-id="element-8012" />
            {t('pages.getStatusBadge.paid')}
          </span>;
      case 'pending':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-8013">
            <ClockIcon size={12} className="mr-1" data-id="element-8014" />
            {t('components.renderSchedulePreview.pending')}
          </span>;
      case 'expired':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-8015">
            <XCircleIcon size={12} className="mr-1" data-id="element-8016" />
            {t('components.CardOnFilePayment.expired')}
          </span>;
      case 'failed':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-8017">
            <AlertCircleIcon size={12} className="mr-1" data-id="element-8018" />
            {t('components.getStatusBadge.failed')}
          </span>;
      case 'cancelled':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-8019">
            <XCircleIcon size={12} className="mr-1" data-id="element-8020" />
            {t('pages.getStatusBadge.cancelled')}
          </span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500" data-id="element-8021">
            {status}
          </span>;
    }
  };
  // Navigate back to the payment form with bulk tab selected
  const handleNavigateBack = () => {
    navigate('/payment-requests?tab=bulk');
  };
  const pendingCount = paymentItems.filter(p => p.status === 'pending').length;
  return <div className="space-y-8 pb-12" data-id="element-8022">
      {/* Back button */}
      <button onClick={handleNavigateBack} className="flex items-center text-[#B38A00] hover:text-[#8C6C00] text-sm font-medium transition-colors" data-id="element-8023">
        <ArrowLeftIcon size={16} className="mr-1.5" data-id="element-8024" />
        {t('pages.BulkPaymentDetail.back_to_payment_form')}
      </button>

      {/* Header & Actions */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4" data-id="element-8025">
        <div data-id="element-8026">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-8027">{batch.name}</h2>
          <p className="text-sm text-gray-500 mt-1" data-id="element-8028">
            {t('pages.BulkPaymentDetail.created_on')}{' '}
            {formatDate(batch.createdAt)}
          </p>
        </div>
        <div className="flex space-x-3" data-id="element-8029">
          <button className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors shadow-sm" data-id="element-8030">
            <DownloadIcon size={16} className="mr-2" data-id="element-8031" />
            {t('pages.BulkPaymentDetail.export')}
          </button>
          <button onClick={() => setShowReminderModal(true)} className="flex items-center px-4 py-2 text-sm font-medium text-gray-900 bg-[#FFC70A] hover:bg-[#D9A800] rounded-lg transition-colors shadow-sm" data-id="element-8032">
            <SendIcon size={16} className="mr-2" data-id="element-8033" />
            {t('pages.BulkPaymentDetail.send_reminders')}
          </button>
        </div>
      </div>

      {/* KPIs Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6" data-id="element-8034">
        <div className="bg-white border border-gray-200 p-5 rounded-xl shadow-sm" data-id="element-8035">
          <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-8036">
            {t('components.BulkPaymentList.total_amount')}
          </div>
          <div className="text-2xl font-bold text-gray-900" data-id="element-8037">
            {formatCurrencyAmount(batch.totalAmount, {
            currency: batch.currency,
            fractionDigits: 2
          })}
          </div>
        </div>

        <div className="bg-white border border-gray-200 p-5 rounded-xl shadow-sm" data-id="element-8038">
          <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-8039">
            {t('pages.BulkPaymentDetail.collection_rate')}
          </div>
          <div className="flex items-center mt-1" data-id="element-8040">
            <div className="text-2xl font-bold text-gray-900 mr-3" data-id="element-8041">
              {batch.collectionRate}%
            </div>
            <div className="w-full max-w-[100px] bg-gray-100 rounded-full h-2" data-id="element-8042">
              <div className={`h-2 rounded-full ${batch.collectionRate >= 80 ? 'bg-green-500' : batch.collectionRate >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{
              width: `${batch.collectionRate}%`
            }} data-id="element-8043"></div>
            </div>
          </div>
          <div className="text-xs text-gray-500 mt-1.5 font-medium" data-id="element-8044">
            {batch.completedItems} {t('components.BulkPaymentList.of')}{' '}
            {batch.totalItems} {t('components.BulkPaymentList.paid')}
          </div>
        </div>

        <div className="bg-white border border-gray-200 p-5 rounded-xl shadow-sm" data-id="element-8045">
          <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-8046">
            {t('components.BulkPaymentList.status')}
          </div>
          <div className="flex items-center mt-1" data-id="element-8047">
            {batch.status === 'completed' ? <CheckCircleIcon size={20} className="text-green-500 mr-2" data-id="element-8048" /> : batch.status === 'processing' ? <ClockIcon size={20} className="text-blue-500 mr-2" data-id="element-8049" /> : <AlertCircleIcon size={20} className="text-red-500 mr-2" data-id="element-8050" />}
            <span className="text-xl font-bold text-gray-900 capitalize" data-id="element-8051">
              {batch.status}
            </span>
          </div>
        </div>

        <div className="bg-white border border-gray-200 p-5 rounded-xl shadow-sm" data-id="element-8052">
          <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-8053">
            {t('pages.BulkPaymentDetail.payment_requests')}
          </div>
          <div className="text-2xl font-bold text-gray-900" data-id="element-8054">
            {batch.totalItems}
          </div>
          <div className="flex items-center text-xs font-medium text-gray-500 mt-1.5" data-id="element-8055">
            <RefreshCwIcon size={12} className="mr-1" data-id="element-8056" />
            {t('pages.BulkPaymentDetail.last_updated_5_mins_ago')}
          </div>
        </div>
      </div>

      {/* Payment requests list */}
      <div className="pt-8 border-t border-gray-200" data-id="element-8057">
        <h3 className="text-lg font-semibold text-gray-900 mb-6" data-id="element-8058">
          {t('pages.BulkPaymentDetail.payment_requests')}
        </h3>

        {/* Search and filter */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 space-y-4 sm:space-y-0" data-id="element-8059">
          <div className="flex flex-wrap gap-2" data-id="element-8060">
            <button className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${statusFilter === null ? 'bg-[#FFC70A]/10 text-[#B38A00]' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} onClick={() => setStatusFilter(null)} data-id="element-8061">
              {t('pages.BulkPaymentDetail.all')}
              {paymentItems.length})
            </button>
            {Object.entries(statusCounts).map(([status, count]) => <button key={status} className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${statusFilter === status ? 'bg-[#FFC70A]/10 text-[#B38A00]' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} onClick={() => setStatusFilter(status === statusFilter ? null : status)} data-id="element-8062">
                {status.charAt(0).toUpperCase() + status.slice(1)} ({count})
              </button>)}
          </div>
          <div className="relative w-full sm:w-72" data-id="element-8063">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-8064">
              <SearchIcon size={16} className="text-gray-400" data-id="element-8065" />
            </div>
            <input type="search" aria-label={t('pages.BulkPaymentDetail.attrs.search_payments')} className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-[#FFC70A] focus:border-[#FFC70A] text-sm" placeholder={t('pages.BulkPaymentDetail.attrs.search_payments')} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-8066" />
          </div>
        </div>

        {/* Payments table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-id="element-8067">
          <div className="overflow-x-auto" data-id="element-8068">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-8069">
              <thead className="bg-gray-50" data-id="element-8070">
                <tr data-id="element-8071">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8072">
                    {t('components.BulkPaymentPreview.customer')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8073">
                    {t('components.BulkPaymentPreview.amount')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8074">
                    {t('components.BulkPaymentPreview.description')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8075">
                    {t('components.BulkPaymentPreview.reference')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8076">
                    {t('components.BulkPaymentList.status')}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8077">
                    {t('components.BulkPaymentList.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-8078">
                {filteredPayments.map(payment => <tr key={payment.id} className="hover:bg-gray-50 transition-colors group" data-id="element-8079">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-8080">
                      <div className="text-sm font-medium text-gray-900" data-id="element-8081">
                        {payment.customerName}
                      </div>
                      <div className="text-sm text-gray-500" data-id="element-8082">
                        {payment.email}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-8083">
                      <div className="text-sm font-medium text-gray-900" data-id="element-8084">
                        {formatCurrencyAmount(parseFloat(payment.amount), {
                      currency: payment.currency,
                      fractionDigits: 2
                    })}
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-8085">
                      <div className="text-sm text-gray-500 max-w-xs truncate" data-id="element-8086">
                        {payment.description}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-8087">
                      {payment.reference}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-8088">
                      {getStatusBadge(payment.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-8089">
                      <div className="relative inline-block text-left" data-id="element-8090">
                        <button aria-label="Payment actions" className="p-1 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100 transition-colors" data-id="element-8091">
                          <MoreHorizontalIcon size={18} aria-hidden="true" data-id="element-8092" />
                        </button>
                        <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-100 py-1 z-10 hidden group-hover:block" data-id="element-8093">
                          <button className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-8094">
                            <SendIcon size={16} className="mr-3 text-gray-400" data-id="element-8095" />

                            {t('pages.BulkPaymentDetail.resend')}
                          </button>
                          <button className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-8096">
                            <FileTextIcon size={16} className="mr-3 text-gray-400" data-id="element-8097" />

                            {t('pages.BulkPaymentDetail.view_details')}
                          </button>
                          {payment.status === 'pending' && <button className="flex w-full items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50" data-id="element-8098">
                              <XCircleIcon size={16} className="mr-3 text-red-500" data-id="element-8099" />

                              {t('pages.BulkPaymentDetail.cancel_request')}
                            </button>}
                        </div>
                      </div>
                    </td>
                  </tr>)}
                {filteredPayments.length === 0 && <tr data-id="element-8100">
                    <td colSpan={6} className="px-6 py-12 text-center text-sm text-gray-500" data-id="element-8101">
                      {t('pages.BulkPaymentDetail.no_payment_requests_found_matching_your')}
                    </td>
                  </tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Reminder Confirmation Modal */}
      {showReminderModal && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-id="element-8102">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full overflow-hidden" data-id="element-8103">
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center" data-id="element-8104">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-8105">
                {t('pages.BulkPaymentDetail.send_reminders')}
              </h3>
              <button onClick={() => setShowReminderModal(false)} aria-label="Close" className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-8106">
                <XIcon size={20} aria-hidden="true" data-id="element-8107" />
              </button>
            </div>
            <div className="p-6" data-id="element-8108">
              <p className="text-sm text-gray-600" data-id="element-8109">
                {t('pages.BulkPaymentDetail.this_will_send_payment_reminders_to')}{' '}
                <span className="font-semibold text-gray-900" data-id="element-8110">
                  {pendingCount}
                </span>{' '}
                {t('pages.BulkPaymentDetail.customers_with_pending_payments')}
              </p>
            </div>
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end space-x-3" data-id="element-8111">
              <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors shadow-sm" onClick={() => setShowReminderModal(false)} data-id="element-8112">
                {t('components.CardOnFilePayment.cancel')}
              </button>
              <button className="px-4 py-2 text-sm font-medium text-gray-900 bg-[#FFC70A] hover:bg-[#D9A800] rounded-lg transition-colors shadow-sm" onClick={() => setShowReminderModal(false)} data-id="element-8113">
                {t('pages.BulkPaymentDetail.send_reminders')}
              </button>
            </div>
          </div>
        </div>}
    </div>;
}